import React from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Formik, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'

import {
  SERVICE_COLORS,
  LINK_REGEX,
  BOOKING_TYPES_ICONS,
  BOOKING_TYPE_IMAGE_SIZE,
} from '../../../helpers/constants'
import { getAssetLink } from '../../../helpers/get-asset-link'
import TextAreaField from '../../FormLegacy/FormFieldsV2/TextAreaField'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import ImageField from '../../FormLegacy/FormFieldsV2/ImageField'
import RadioField from '../../FormLegacy/FormFieldsV2/RadioField'
import Filter from '../../Filter/Filter'
import Loader from '../../Loader/Loader'
import BuildingAnalyticIcons from '../../BuildingAnalytics/BuildingAnalyticIcons/BuildingAnalyticIcons'
import {
  selectBookTypeById,
  addBookType,
  updateBookType,
  fetchOneBookingTypeOnly,
  removeBookType,
} from '../../../reducers/Organisation/bookTypesReducer'
import { fetchServicesQuestions } from '../../../reducers/serviceFormReducer'
import ConfirmPopup from '../../Popups/ConfirmPopup'

const BookingTypeModal = ({
  params,
  addBookType,
  updateBookType,
  onModalClose,
  bookTypeOrder,
  bookingType,
  buildings,
  fetchOneBookingTypeOnly,
  removeBookType,
  fetchServicesQuestions,
}) => {
  const { t } = useTranslation(['organisation', 'common'])
  const [formPending, setFormPending] = React.useState(false)
  const [formError, setFormError] = React.useState('')
  const [modalConfirmOpened, setModalConfirmOpened] = React.useState(false)

  React.useEffect(() => {
    params.bookingId && params.bookingId !== 'new' && fetchOneBookingTypeOnly(params.bookingId)
  }, [params.bookingId])

  const closeModal = () => {
    onModalClose()
    setFormPending(false)
  }

  const cancelServiceChanges = (e) => {
    e.preventDefault()
    closeModal()
  }

  const isNew = () => params.bookingId === 'new'

  const isDuplicate = () => params.mode === 'duplicate'

  const saveBookingType = ({
    name,
    internalName,
    buildings,
    icon,
    color,
    description,
    link,
    image,
    order,
    category,
  }) => {
    setFormPending(true)
    setFormError('')

    if (isNew() || isDuplicate()) {
      const buildingsIds = buildings.map((building) => building.id)
      const bookingType = {
        name,
        internalName,
        buildings: JSON.stringify(buildingsIds),
        icon,
        color,
        description,
        link,
        order,
        category,
        active: true,
        image,
      }

      if (!isDuplicate()) {
        if (image.file) {
          if (image.file.size > BOOKING_TYPE_IMAGE_SIZE) {
            setFormPending(false)
            return setFormError(t('organisation:booking.typeModal.errors.imageTooLarge'))
          }
          bookingType.image = image.file
        } else {
          setFormPending(false)
          return setFormError(t('organisation:booking.typeModal.errors.imageRequired'))
        }
      }

      if (isDuplicate()) {
        bookingType.duplicateId = params.bookingId
      }

      addBookType(bookingType)
        .then(() => {
          closeModal()
          setFormPending(false)

          if (isDuplicate()) {
            fetchServicesQuestions()
          }
        })
        .catch((err) => {
          setFormPending(false)
          setFormError(err.message || t('organisation:shared.serverError'))
          throw new Error(err)
        })
    } else {
      const updateData = {
        name,
        internalName,
        buildings,
        icon,
        color,
        image,
        description,
        link,
      }

      if (image.file.size > BOOKING_TYPE_IMAGE_SIZE) {
        setFormPending(false)
        return setFormError(t('organisation:booking.typeModal.errors.imageTooLarge'))
      }

      updateBookType(params.bookingId, updateData)
        .then(() => {
          closeModal()
          setFormPending(false)
        })
        .catch((err) => {
          setFormPending(false)
          setFormError(t('organisation:shared.serverError'))
          throw new Error(err)
        })
    }
  }

  const openConfirmModal = (e) => {
    e.preventDefault()
    setModalConfirmOpened(true)
  }

  const closeConfirmModal = () => {
    setModalConfirmOpened(false)
  }

  const removeBookingType = () => {
    removeBookType(bookingType.id)
      .then(() => {
        closeConfirmModal()
        closeModal()
      })
      .catch(() => {
        setFormError(t('organisation:shared.serverError'))
        closeConfirmModal()
      })
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('common:validation.required')),
    internalName: Yup.string().required(t('common:validation.required')),
    buildings: Yup.array().required(),
    description: Yup.string(),
    link: Yup.string().matches(LINK_REGEX),
    icon: Yup.string().required(t('organisation:booking.typeModal.errors.iconRequired')),
    color: Yup.string().required(t('organisation:booking.typeModal.errors.colorRequired')),
  })

  const getImage = (imageFile) =>
    imageFile.image && imageFile.image.startsWith('data:image')
      ? imageFile
      : {
          ...imageFile,
          image: getAssetLink({ path: 'chitown-images/booking', file: imageFile.image }),
        }

  return (
    <div
      className={`modal modal--organisation modal--service ${params.bookingId ? 'is-active' : ''}`}
    >
      <div className="modal-background" onClick={cancelServiceChanges} />
      <div className="modal-content">
        <div className="box">
          <header className="modal__header">
            <span>
              {isNew()
                ? t('organisation:booking.typeModal.titleNew')
                : isDuplicate()
                  ? t('organisation:booking.typeModal.titleDuplicate')
                  : t('organisation:booking.typeModal.titleEdit')}
            </span>
            <button
              className="modal-close is-large"
              aria-label={t('organisation:shared.closeAriaLabel')}
              onClick={cancelServiceChanges}
            />
          </header>
          {params.bookingId && params.bookingId !== 'new' && !bookingType.buildings ? (
            <Loader />
          ) : (
            <Formik
              enableReinitialize={true}
              initialValues={{
                name: bookingType.name || '',
                buildings: bookingType.buildings ? bookingType.buildings : [],
                icon: bookingType.icon || '',
                color: bookingType.color || '',
                description: bookingType.description || '',
                link: bookingType.link || '',
                image: {
                  file: false,
                  image: bookingType.image || '',
                },
                order: bookTypeOrder || null,
                category: parseInt(params.categoryId) || null,
                internalName: isDuplicate() ? '' : bookingType.internalName || '',
              }}
              onSubmit={saveBookingType}
              validationSchema={validationSchema}
            >
              {({
                handleSubmit,
                handleChange,
                values,
                errors,
                touched,
                setFieldTouched,
                setFieldValue,
                dirty,
              }) => {
                return (
                  <form onSubmit={handleSubmit} className="modal__inner form">
                    <div className="columns">
                      <div className="column">
                        <div className="modal__section modal__main-info">
                          <div
                            className="modal__icon"
                            style={{ backgroundColor: values.color, color: '#ffffff' }}
                          >
                            <BuildingAnalyticIcons name={values.icon} />
                          </div>
                          <div className="modal__name">
                            <TextField
                              name={'name'}
                              placeholder={t('organisation:services.modal.name.placeholder')}
                              label={t('organisation:services.modal.name.label')}
                              value={values.name}
                              isError={touched.name && !!errors.name}
                              onChange={handleChange}
                              onBlur={() => setFieldTouched('name')}
                            />
                            <TextField
                              name={'internalName'}
                              label={t('organisation:services.modal.internalName.label')}
                              onChange={handleChange}
                              value={values.internalName}
                              placeholder={t(
                                'organisation:services.modal.internalName.placeholder',
                              )}
                              onBlur={() => setFieldTouched('internalName')}
                              isError={touched.internalName && !!errors.internalName}
                            />
                          </div>
                        </div>
                        <div className="modal__section modal__icons booking--type--icons">
                          <span className="modal__section-label">
                            {t('organisation:services.modal.chooseIcon')}
                          </span>
                          <ul>
                            {BOOKING_TYPES_ICONS.map((icon) => {
                              return (
                                <li key={icon}>
                                  <RadioField
                                    name={'icon'}
                                    value={icon}
                                    checked={values.icon === icon}
                                    label={<BuildingAnalyticIcons name={icon} />}
                                    onChange={handleChange}
                                  />
                                </li>
                              )
                            })}
                            <span style={{ fontSize: 12, color: 'red' }}>
                              <ErrorMessage name={'icon'} />
                            </span>
                          </ul>
                        </div>
                        <div className="modal__section modal__colors">
                          <span className="modal__section-label">
                            {t('organisation:services.modal.chooseColor')}
                          </span>
                          <ul>
                            {SERVICE_COLORS.map((color) => {
                              return (
                                <li key={color}>
                                  <RadioField
                                    name={'color'}
                                    value={color}
                                    checked={values.color === color}
                                    label={
                                      <span
                                        className="color-label"
                                        style={{ backgroundColor: color }}
                                      />
                                    }
                                    onChange={handleChange}
                                  />
                                </li>
                              )
                            })}
                            <span style={{ fontSize: 12, color: 'red' }}>
                              <ErrorMessage name={'color'} />
                            </span>
                          </ul>
                        </div>
                        <div className="modal__section modal__category">
                          <Filter
                            name="buildings"
                            placeholder={t(
                              'organisation:booking.typeModal.buildingAvailability.placeholder',
                            )}
                            label={t('organisation:booking.typeModal.buildingAvailability.label')}
                            additionalClass="modal--category__filter"
                            options={[
                              {
                                id: -1,
                                text: t('organisation:propertyManagers.list.allBuildingsOption'),
                                avatar: 'building',
                                selectAll: true,
                              },
                              ...buildings,
                            ]}
                            value={values.buildings}
                            onChange={handleChange}
                            config={{
                              multiple: true,
                              hasSearch: true,
                            }}
                          />
                          <TextField
                            name={'link'}
                            placeholder={t('organisation:booking.typeModal.link.placeholder')}
                            label={t('organisation:booking.typeModal.link.label')}
                            value={values.link}
                            isError={touched.link && !!errors.link}
                            onChange={handleChange}
                            onBlur={() => setFieldTouched('link')}
                            style={{ width: '80%' }}
                          />
                        </div>
                      </div>
                      <div className="column">
                        <div className="modal__section">
                          <ImageField
                            name={'image'}
                            label={t('organisation:services.modal.imageInCategory.label')}
                            placeholderImage="/images/placeholder.png"
                            imageRatio={16 / 9}
                            onChange={(e) => setFieldValue('image', e.target.value)}
                            value={getImage(values.image)}
                            additionalClass={'field-image--big'}
                          />
                        </div>
                        <div className="modal__section">
                          <br />
                          <TextAreaField
                            name={'description'}
                            placeholder={t('organisation:services.modal.description.placeholder')}
                            label={t('organisation:services.modal.description.label')}
                            value={values.description}
                            onChange={handleChange}
                            isError={touched.description && !!errors.description}
                            onBlur={() => setFieldTouched('description')}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="modal__section modal__actions">
                      {!isNew() && !isDuplicate() && (
                        <span className="link" onClick={openConfirmModal}>
                          {t('organisation:booking.typeModal.delete')}
                        </span>
                      )}
                      <button
                        type={'button'}
                        className="button button-gray is-block"
                        onClick={cancelServiceChanges}
                      >
                        {t('common:actions.cancel')}
                      </button>
                      <button
                        type={'submit'}
                        className={`button button-primary is-block ${
                          formPending ? 'is-loading' : ''
                        }`}
                        disabled={formPending || !dirty}
                      >
                        {isDuplicate()
                          ? t('organisation:services.item.duplicate')
                          : isNew()
                            ? t('organisation:shared.actions.create')
                            : t('common:actions.save')}
                      </button>
                      <p className={'help is-danger'}>{formError}</p>
                    </div>
                  </form>
                )
              }}
            </Formik>
          )}
        </div>
      </div>
      <ConfirmPopup
        title={t('organisation:booking.typeModal.deleteConfirm.title', { name: bookingType.name })}
        cancelText={t('common:actions.cancel')}
        confirmText={t('common:actions.confirm')}
        isOpen={modalConfirmOpened}
        cancelCallback={closeConfirmModal}
        confirmCallback={removeBookingType}
      />
    </div>
  )
}

BookingTypeModal.propTypes = {
  params: PropTypes.object,
  bookingType: PropTypes.object,
  buildings: PropTypes.array.isRequired,
  addBookType: PropTypes.func.isRequired,
  bookTypeOrder: PropTypes.number,
  onModalClose: PropTypes.func.isRequired,
  updateBookType: PropTypes.func.isRequired,
  fetchOneBookingTypeOnly: PropTypes.func,
  removeBookType: PropTypes.func,
  fetchServicesQuestions: PropTypes.func,
}

export default connect(
  (state, { params }) => ({
    bookingType: selectBookTypeById(state)(params.bookingId),
  }),
  { addBookType, updateBookType, fetchOneBookingTypeOnly, removeBookType, fetchServicesQuestions },
)(BookingTypeModal)
